class CartRemoveButton extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('click', (event) => {
      event.preventDefault();
      
      // Check if this is a SmartVibes delete button
      const pairId = this.dataset.pairId;
      if (pairId) {
        console.log(`🔗 SMARTVIBES DELETE: Clicked delete on SmartVibes with pair ID: ${pairId}`);
        
        // Find the paired Apollo item
        const cartItemsElement = this.closest('cart-items');
        const apolloElement = cartItemsElement.querySelector(`[data-handle="apollo-wearable"][data-paired-with="${pairId}"]`);
        
        if (apolloElement) {
          const apolloIndex = apolloElement.querySelector('[data-index]')?.dataset.index;
          console.log(`✅ SMARTVIBES DELETE: Found paired Apollo at index ${apolloIndex}, deleting it`);
          cartItemsElement.updateQuantity(apolloIndex, 0);
        } else {
          console.log(`⚠️ SMARTVIBES DELETE: No paired Apollo found, deleting SmartVibes directly`);
          this.closest('cart-items').updateQuantity(this.dataset.index, 0);
        }
      } else {
        // Normal delete button (Apollo or other items)
        this.closest('cart-items').updateQuantity(this.dataset.index, 0);
      }
    });
  }
}
customElements.define('cart-remove-button', CartRemoveButton);

class CartItems extends HTMLElement {
  constructor() {
    super();
    console.log('🏗️ CART ITEMS: Constructor called', {
      element: this,
      id: this.id,
      className: this.className
    });
    
    // Add form submit listener to detect any form submissions
    const forms = document.querySelectorAll('form[action*="cart"]');
    forms.forEach(form => {
      form.addEventListener('submit', (e) => {
        console.log('🚨 FORM SUBMIT DETECTED!', {
          form: form,
          action: form.action,
          method: form.method,
          id: form.id,
          className: form.className,
          event: e
        });
      });
    });

    this.lineItemStatusElement = document.getElementById('shopping-cart-line-item-status');
    this.cartErrors = document.getElementById('cart-errors');

    this.currentItemCount = Array.from(this.querySelectorAll('[name="updates[]"]'))
      .reduce((total, quantityInput) => total + parseInt(quantityInput.value), 0);

    this.debouncedOnChange = debounce((event) => {
      this.onChange(event);
    }, 300);

    this.addEventListener('change', this.debouncedOnChange.bind(this));
  }

  onChange(event) {
    if (event.target === null) return;
    
    console.log('🚨 CART ITEMS: onChange called!', {
      element: this,
      elementId: this.id,
      elementClass: this.className,
      target: event.target,
      targetValue: event.target.value,
      targetIndex: event.target.dataset.index,
      isInMiniCart: this.closest('mini-cart') !== null
    });
    
    // Check global flag to prevent interference with synchronized products
    if (window.SYNCHRONIZED_PRODUCTS_UPDATING) {
      console.log('🛡️ CART ITEMS: Synchronized products is updating globally, ignoring change event');
      return;
    }
    
    // Check if synchronized products system is handling this change
    const syncedProductsElement = document.querySelector('synchronized-products');
    if (syncedProductsElement && syncedProductsElement.isUpdating) {
      console.log('🔒 CART ITEMS: Synchronized products is updating, ignoring change event');
      return;
    }
    
    // CRITICAL: Block SmartVibes quantity changes (it's controlled by Apollo Wearable)
    // Allow Apollo Wearable changes so the synchronized-products system can handle syncing
    const variantId = event.target.dataset.quantityVariantId;
    if (variantId === '43854575632581') { // SmartVibes only
      const newQuantity = parseInt(event.target.value);
      
      // Allow deletions (quantity = 0) but block other quantity changes
      if (newQuantity === 0) {
        console.log(`🗑️ CART ITEMS: Allowing SmartVibes deletion (quantity = 0)`);
        // Continue with deletion - don't return
      } else {
        console.log(`🚫 CART ITEMS: Ignoring SmartVibes quantity change (controlled by parent)`);
        return;
      }
    }
    
    // Check if the input is locked by synchronized products
    if (event.target.dataset.locked === 'true') {
      console.log('🔒 CART ITEMS: Input is locked by synchronized products, ignoring change event');
      return;
    }
    
    console.log('✅ CART ITEMS: Processing change event', {
      target: event.target,
      value: event.target.value,
      index: event.target.dataset.index
    });
    
    this.updateQuantity(event.target.dataset.index, event.target.value, document.activeElement.getAttribute('name'));
  }

  getSectionsToRender() {
    let sections = [
      {
        id: 'mini-cart',
        section: document.getElementById('mini-cart')?.id,
        selector: '.shopify-section',
      },
      {
        id: 'main-cart-items',
        section: document.getElementById('main-cart-items')?.dataset.id,
        selector: '.js-contents',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section'
      },
      {
        id: 'mobile-cart-icon-bubble',
        section: 'mobile-cart-icon-bubble',
        selector: '.shopify-section'
      },
      {
        id: 'cart-live-region-text',
        section: 'cart-live-region-text',
        selector: '.shopify-section'
      },
      {
        id: 'main-cart-footer',
        section: document.getElementById('main-cart-footer')?.dataset.id,
        selector: '.js-contents',
      }
    ];
    if (document.querySelector('#main-cart-footer .free-shipping')) {
      sections.push({
        id: 'main-cart-footer',
        section: document.getElementById('main-cart-footer')?.dataset.id,
        selector: '.free-shipping',
      });
    }
    return sections;
  }

  updateQuantity(line, quantity, name) {
    console.log('🚨 CART ITEMS: updateQuantity called!', {
      line: line,
      quantity: quantity,
      name: name,
      element: this
    });
    
    // CRITICAL: Capture pair ID BEFORE any deletion happens
    let pairIdToDelete = null;
    if (quantity === 0) {
      const lineElement = this.querySelector(`[data-index="${line}"]`);
      if (lineElement) {
        const itemElement = lineElement.closest('[data-handle]');
        pairIdToDelete = itemElement?.dataset.pairedWith;
        if (pairIdToDelete) {
          console.log(`🔗 CART ITEMS: Captured pair ID BEFORE deletion: ${pairIdToDelete}`);
        }
      }
    }
    
    // Check global flag to prevent interference with synchronized products
    if (window.SYNCHRONIZED_PRODUCTS_UPDATING) {
      console.log('🛡️ CART ITEMS: Synchronized products is updating globally, blocking updateQuantity');
      return;
    }
    
    // Handle deletions (quantity = 0) for synchronized products
    if (quantity === 0) {
      const lineElement = this.querySelector(`[data-index="${line}"]`);
      if (lineElement) {
        const itemElement = lineElement.closest('[data-handle]');
        const handle = itemElement?.dataset.handle;
        if (handle && (handle === 'apollo-wearable' || handle === 'smartvibes')) {
          console.log(`🗑️ CART ITEMS: Deletion detected for ${handle}, using aggressive deletion to bypass external interference`);
          
          // AGGRESSIVE DELETION: Use direct API call to bypass external interference
          const variantId = lineElement.dataset.quantityVariantId;
          const itemKey = itemElement?.dataset.itemKey; // Get from the <li> element
          
          if (itemKey) {
            console.log(`🚀 CART ITEMS: Making direct deletion API call for ${handle} using item key: ${itemKey}`);
            
            // Use /cart/update.js with item key to handle selling plans correctly
            fetch('/cart/update.js', {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                'Accept': 'application/json'
              },
              body: JSON.stringify({
                updates: {
                  [itemKey]: 0  // Use item key instead of variant ID to handle selling plans
                }
              })
            })
            .then(response => response.json())
            .then(data => {
              // Check if API returned an error
              if (data.status === 422 || data.message) {
                console.log(`❌ CART ITEMS: API error during deletion:`, data);
                window.CART_DELETION_IN_PROGRESS = false; // Unblock on error
                return;
              }
              
              console.log(`✅ CART ITEMS: Direct deletion API response:`, data);
              console.log(`📊 CART ITEMS: Cart now has ${data.item_count} items after deletion`);
              console.log(`🔍 CART ITEMS: Cart items breakdown:`, data.items?.map(i => `${i.handle} (${i.variant_id}) key=${i.key}`) || []);
              
              // CRITICAL: Verify the SPECIFIC ITEM (by key) was actually removed from cart
              const remainingItems = data.items || [];
              const stillHasProduct = remainingItems.some(item => item.key === itemKey);
              
              if (stillHasProduct) {
                console.log(`⚠️ CART ITEMS: Item with key ${itemKey} still in cart after deletion`);
              } else {
                console.log(`✅ CART ITEMS: Item with key ${itemKey} successfully removed`);
              }
              
              if (stillHasProduct) {
                console.log(`⚠️ CART ITEMS: Item ${itemKey} still in cart after deletion, retrying...`);
                
                // AGGRESSIVE DUPLICATE REMOVAL: Keep calling API until all instances are gone
                const removeAllInstances = (attemptCount = 1) => {
                  if (attemptCount > 5) {
                    console.log(`❌ CART ITEMS: Max attempts reached, giving up on ${handle} removal`);
                    return;
                  }
                  
                  console.log(`🔄 CART ITEMS: Attempt ${attemptCount} to remove remaining ${handle} duplicates`);
                  
                  fetch('/cart/update.js', {
                    method: 'POST',
                    headers: {
                      'Content-Type': 'application/json',
                      'Accept': 'application/json'
                    },
                    body: JSON.stringify({
                      updates: {
                        [variantId]: 0
                      }
                    })
                  })
                  .then(response => response.json())
                  .then(newData => {
                    const newRemainingItems = newData.items || [];
                    const stillHasProductAfterRetry = newRemainingItems.some(item => item.handle === handle);
                    
                    if (stillHasProductAfterRetry) {
                      console.log(`🔄 CART ITEMS: ${handle} still in cart after attempt ${attemptCount}, trying again`);
                      setTimeout(() => removeAllInstances(attemptCount + 1), 500);
                    } else {
                      console.log(`✅ CART ITEMS: ${handle} variant ${variantId} completely removed after ${attemptCount} attempts`);
                      
                      // CRITICAL: Fetch cart sections and refresh UI
                      const sections = this.getSectionsToRender().map((section) => section.section);
                      const sectionsParam = sections.join(',');
                      
                      fetch(`/?sections=${sectionsParam}`)
                        .then(response => response.json())
                        .then(sectionsData => {
                          console.log(`🔄 CART ITEMS: Fetched fresh sections for UI refresh after retries`);
                          
                          // Combine cart data with sections data
                          const cartDataWithSections = {
                            ...newData,
                            sections: sectionsData
                          };
                          
                          // Refresh cart UI
                          this.renderContents(cartDataWithSections);
                        })
                        .catch(error => {
                          console.log(`❌ CART ITEMS: Failed to fetch sections after retries:`, error);
                        });
                      
                      // Trigger bidirectional deletion
                      const syncedProductsElement = document.querySelector('synchronized-products');
                      if (syncedProductsElement && syncedProductsElement.handleProductDeletion) {
                        if (!syncedProductsElement.processingDeletions) {
                          syncedProductsElement.processingDeletions = new Set();
                        }
                        syncedProductsElement.processingDeletions.add(handle);
                        syncedProductsElement.handleProductDeletion(handle);
                        setTimeout(() => {
                          if (syncedProductsElement.processingDeletions) {
                            syncedProductsElement.processingDeletions.delete(handle);
                          }
                        }, 2000);
                      }
                    }
                  })
                  .catch(error => {
                    console.log(`❌ CART ITEMS: Error in attempt ${attemptCount}:`, error);
                  });
                };
                
                removeAllInstances();
              } else {
                console.log(`✅ CART ITEMS: ${handle} variant ${variantId} completely removed from cart`);
                
                // CRITICAL: Use the captured pair ID to delete paired item
                if (pairIdToDelete) {
                  console.log(`🔗 CART ITEMS: Deleted item has pair ID: ${pairIdToDelete}, finding and deleting paired item`);
                  console.log(`🛡️ CART ITEMS: Blocking external cart updates during paired deletion`);
                  
                  // Block external cart updates
                  window.CART_DELETION_IN_PROGRESS = true;
                  
                  // Find paired item in cart data
                  console.log(`🔍 CART ITEMS: Searching for paired item with pair ID: ${pairIdToDelete}`);
                  console.log(`🔍 CART ITEMS: Current cart items:`, data.items.map(i => ({
                    handle: i.handle,
                    variantId: i.variant_id,
                    pairId: i.properties?._paired_with,
                    key: i.key,
                    matchesPairId: i.properties?._paired_with === pairIdToDelete,
                    differentVariant: i.variant_id.toString() !== variantId.toString()
                  })));
                  
                  const pairedItem = data.items.find(item => 
                    item.properties && item.properties._paired_with === pairIdToDelete && item.variant_id.toString() !== variantId.toString()
                  );
                  
                  console.log(`🔍 CART ITEMS: Paired item search result:`, pairedItem ? {
                    handle: pairedItem.handle,
                    variantId: pairedItem.variant_id,
                    key: pairedItem.key
                  } : 'NOT FOUND');
                  
                  if (pairedItem) {
                    console.log(`🗑️ CART ITEMS: Found paired item to delete:`, {
                      handle: pairedItem.handle,
                      variantId: pairedItem.variant_id,
                      key: pairedItem.key
                    });
                    
                    // Delete paired item
                    const updates = {};
                    updates[pairedItem.key] = 0;
                    
                    fetch('/cart/update.js', {
                      method: 'POST',
                      headers: {
                        'Content-Type': 'application/json',
                        'Accept': 'application/json'
                      },
                      body: JSON.stringify({ updates })
                    })
                    .then(response => response.json())
                    .then(updatedCart => {
                      console.log(`✅ CART ITEMS: Paired item deleted, cart now has ${updatedCart.item_count} items`);
                      
                      // Immediately refresh UI with updated cart AFTER paired deletion
                      const sections = this.getSectionsToRender().map((section) => section.section);
                      const sectionsParam = sections.join(',');
                      
                      fetch(`/?sections=${sectionsParam}`)
                        .then(response => response.json())
                        .then(sectionsData => {
                          console.log(`🔄 CART ITEMS: Fetched fresh sections after paired deletion`);
                          
                          // CRITICAL: Unblock BEFORE calling renderContents
                          window.CART_DELETION_IN_PROGRESS = false;
                          console.log(`✅ CART ITEMS: Paired deletion complete, unblocking external updates`);
                          
                          this.renderContents({ ...updatedCart, sections: sectionsData });
                          
                          // Update cart count
                          const cartIconBubble = document.getElementById('cart-icon-bubble');
                          if (cartIconBubble) {
                            const tempDiv = document.createElement('div');
                            tempDiv.innerHTML = sectionsData['cart-icon-bubble'];
                            const bubble = tempDiv.querySelector('.cart-count-bubble');
                            const existingBubble = cartIconBubble.querySelector('.cart-count-bubble');
                            if (bubble && existingBubble) {
                              existingBubble.innerHTML = bubble.innerHTML;
                            }
                          }
                        })
                        .catch(error => {
                          console.log(`❌ CART ITEMS: Failed to fetch sections after paired deletion:`, error);
                          window.CART_DELETION_IN_PROGRESS = false;
                        });
                    })
                    .catch(error => {
                      console.log(`❌ CART ITEMS: Failed to delete paired item:`, error);
                      window.CART_DELETION_IN_PROGRESS = false; // Ensure unblocked on error
                      
                      // Still refresh UI even if paired deletion failed
                      const sections = this.getSectionsToRender().map((section) => section.section);
                      const sectionsParam = sections.join(',');
                      
                      fetch(`/?sections=${sectionsParam}`)
                        .then(response => response.json())
                        .then(sectionsData => {
                          this.renderContents({ ...data, sections: sectionsData });
                        });
                    });
                  } else {
                    console.log(`⚠️ CART ITEMS: No paired item found with pair ID: ${pairIdToDelete}`);
                    window.CART_DELETION_IN_PROGRESS = false; // Unblock
                    
                    // No paired item found, just refresh UI
                    const sections = this.getSectionsToRender().map((section) => section.section);
                    const sectionsParam = sections.join(',');
                    
                    fetch(`/?sections=${sectionsParam}`)
                      .then(response => response.json())
                      .then(sectionsData => {
                        this.renderContents({ ...data, sections: sectionsData });
                      });
                  }
                } else {
                  console.log(`ℹ️ CART ITEMS: No pair ID captured, no paired deletion needed`);
                  window.CART_DELETION_IN_PROGRESS = false; // Unblock
                  
                  // No pairing, just refresh UI
                  const sections = this.getSectionsToRender().map((section) => section.section);
                  const sectionsParam = sections.join(',');
                  
                  fetch(`/?sections=${sectionsParam}`)
                    .then(response => response.json())
                    .then(sectionsData => {
                      console.log(`🔄 CART ITEMS: Fetched fresh sections for UI refresh`);
                      this.renderContents({ ...data, sections: sectionsData });
                      
                      // Update cart count
                      const cartIconBubble = document.getElementById('cart-icon-bubble');
                      if (cartIconBubble) {
                        const tempDiv = document.createElement('div');
                        tempDiv.innerHTML = sectionsData['cart-icon-bubble'];
                        const bubble = tempDiv.querySelector('.cart-count-bubble');
                        const existingBubble = cartIconBubble.querySelector('.cart-count-bubble');
                        if (bubble && existingBubble) {
                          existingBubble.innerHTML = bubble.innerHTML;
                        }
                      }
                    })
                    .catch(error => {
                      console.log(`❌ CART ITEMS: Failed to fetch sections:`, error);
                    });
                }
              }
            })
            .catch(error => {
              console.log(`❌ CART ITEMS: Direct deletion failed for ${handle}:`, error);
              window.CART_DELETION_IN_PROGRESS = false; // Ensure unblocked on error
            });
            
            // Return early to prevent normal updateQuantity flow
            return;
          }
        }
      }
    }
    
    this.enableLoading(line);
    const sections = this.getSectionsToRender().map((section) => section.section);

    const body = JSON.stringify({
      line,
      quantity,
      sections: sections,
      sections_url: window.location.pathname
    });

    fetch(`${theme.routes.cart_change_url}`, {...fetchConfig(), ...{ body }})
      .then((response) => {
        return response.text();
      })
      .then((state) => {
        const parsedState = JSON.parse(state);
        this.classList.toggle('is-empty', parsedState.item_count === 0);
        const cartFooter = document.getElementById('main-cart-footer');

        if (cartFooter) cartFooter.classList.toggle('is-empty', parsedState.item_count === 0);
        if (parsedState.errors) {
          this.updateErrorLiveRegions(line, parsedState.errors);
        }
        this.getSectionsToRender().forEach((section => {
          const element = document.getElementById(section.id);
          if (element) {
            const elementToReplace = element.querySelector(section.selector) || element;

            if (elementToReplace && parsedState.sections[section.section]) {
              elementToReplace.innerHTML =
                this.getSectionInnerHTML(parsedState.sections[section.section], section.selector);
            }
          }
        }));
        
        this.updateQuantityLiveRegions(line, parsedState.item_count);
        
        const lineItem = document.getElementById(`CartItem-${line}`);
        if (lineItem && name) lineItem.querySelector(`[name="${name}"]`).focus();
        this.disableLoading();

        document.dispatchEvent(new CustomEvent('cart:updated', {
          detail: {
            cart: state
          }
        }));
        publish(PUB_SUB_EVENTS.cartUpdate, { source: 'cart-items' });
      })
      .catch(() => {
        this.querySelectorAll('.loading-overlay').forEach((overlay) => overlay.classList.add('hidden'));
        this.disableLoading();
        if (this.cartErrors) {
          this.cartErrors.textContent = theme.cartStrings.error;
        }
      });
  }
  
  updateErrorLiveRegions(line, message) {
    const lineItemError =
      document.getElementById(`Line-item-error-${line}`) || document.getElementById(`CartDrawer-LineItemError-${line}`);
    if (lineItemError) lineItemError.querySelector('.cart-item__error-text').innerHTML = message;
  
    this.lineItemStatusElement.setAttribute('aria-hidden', true);
  
    const cartStatus =
      document.getElementById('cart-live-region-text') || document.getElementById('CartDrawer-LiveRegionText');
    cartStatus.setAttribute('aria-hidden', false);
  
    setTimeout(() => {
      cartStatus.setAttribute('aria-hidden', true);
    }, 1000);
  }
  
  updateQuantityLiveRegions(line, itemCount) {
    if (this.currentItemCount === itemCount) {
      const quantityError = document.getElementById(`Line-item-error-${line}`);
      if (quantityError) {
        quantityError.querySelector('.cart-item__error-text')
          .innerHTML = theme.cartStrings.quantityError.replace(
            '[quantity]',
            document.getElementById(`Quantity-${line}`).value
          ); 
      }
    }

    this.currentItemCount = itemCount;
    
    if (this.lineItemStatusElement) this.lineItemStatusElement.setAttribute('aria-hidden', true);

    const cartStatus = document.getElementById('cart-live-region-text');
    if (cartStatus) {
      cartStatus.setAttribute('aria-hidden', false);

      setTimeout(() => {
        cartStatus.setAttribute('aria-hidden', true);
      }, 1e3);
    }
  }

  getSectionInnerHTML(html, selector) {
    return new DOMParser()
      .parseFromString(html, 'text/html')
      .querySelector(selector)?.innerHTML;
  }

  enableLoading(line) {
    const cartItems = document.getElementById('main-cart-items');
    if (cartItems) cartItems.classList.add('cart__items--disabled');

    const loadingOverlay = this.querySelectorAll('.loading-overlay')[line - 1];
    if (loadingOverlay) loadingOverlay.classList.remove('hidden');
    
    document.activeElement.blur();
    if (this.lineItemStatusElement) this.lineItemStatusElement.setAttribute('aria-hidden', false);
  }

  disableLoading() {
    const cartItems = document.getElementById('main-cart-items');
    if (cartItems) cartItems.classList.remove('cart__items--disabled');
  }

  renderContents(parsedState) {
    // Block rendering if paired deletion is in progress
    if (window.CART_DELETION_IN_PROGRESS) {
      console.log(`🛡️ CART ITEMS: Blocking renderContents - paired deletion in progress`);
      return;
    }
    
    this.getSectionsToRender().forEach((section => {
      const element = document.getElementById(section.id);

      if (element && parsedState.sections[section.id]) {
        const innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
        if (innerHTML !== undefined) {
          element.innerHTML = innerHTML;
        }
      }
    }));
  }
}
console.log('🏗️ DEFINING CART-ITEMS CUSTOM ELEMENT');

// Check for existing cart-items elements before defining
const existingCartItems = document.querySelectorAll('cart-items');
console.log('🔍 EXISTING CART-ITEMS ELEMENTS:', {
  count: existingCartItems.length,
  elements: Array.from(existingCartItems).map(el => ({
    id: el.id,
    className: el.className,
    innerHTML: el.innerHTML.substring(0, 100) + '...'
  }))
});

customElements.define('cart-items', CartItems);
console.log('✅ CART-ITEMS CUSTOM ELEMENT DEFINED');

// Check again after defining
setTimeout(() => {
  const cartItemsAfter = document.querySelectorAll('cart-items');
  console.log('🔍 CART-ITEMS ELEMENTS AFTER DEFINITION:', {
    count: cartItemsAfter.length,
    elements: Array.from(cartItemsAfter).map(el => ({
      id: el.id,
      className: el.className,
      isConnected: el.isConnected,
      hasConstructorRun: el.hasOwnProperty('lineItemStatusElement')
    }))
  });
}, 100);

class CartNote extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('change', debounce((event) => {
      const body = JSON.stringify({ note: event.target.value });
      fetch(`${theme.routes.cart_update_url}`, {...fetchConfig(), ...{ body }});
    }, 300));
  }
}
customElements.define('cart-note', CartNote);

class DiscountCode extends HTMLElement {
  constructor() {
    super();

    if (isStorageSupported('session')) {
      this.setupDiscount();

      this.addEventListener('change', (event) => {
        window.sessionStorage.setItem('discount', event.target.value);
      });
    }
  }

  setupDiscount() {
    const discount = window.sessionStorage.getItem('discount');
    if (discount !== null) {
      this.querySelector('input[name="discount"]').value = discount;
    }
  }
}

customElements.define('discount-code', DiscountCode);

class ShippingCalculator extends HTMLElement {
  constructor() {
    super();

    this.setupCountries();
    
    this.errors = this.querySelector('#ShippingCalculatorErrors');
    this.success = this.querySelector('#ShippingCalculatorSuccess');
    this.zip = this.querySelector('#ShippingCalculatorZip');
    this.country = this.querySelector('#ShippingCalculatorCountry');
    this.province = this.querySelector('#ShippingCalculatorProvince');
    this.button = this.querySelector('button');
    this.button.addEventListener('click', this.onSubmitHandler.bind(this));
  }

  setupCountries() {
    if (Shopify && Shopify.CountryProvinceSelector) {
      // eslint-disable-next-line no-new
      new Shopify.CountryProvinceSelector('ShippingCalculatorCountry', 'ShippingCalculatorProvince', {
        hideElement: 'ShippingCalculatorProvinceContainer'
      });
    }
  }

  onSubmitHandler(event) {
    event.preventDefault();
    
    this.errors.classList.add('hidden');
    this.success.classList.add('hidden');
    this.zip.classList.remove('invalid');
    this.country.classList.remove('invalid');
    this.province.classList.remove('invalid');
    this.button.classList.add('loading');
    this.button.setAttribute('disabled', true);

    const body = JSON.stringify({
      shipping_address: {
        zip: this.zip.value,
        country: this.country.value,
        province: this.province.value
      }
    });
    let sectionUrl = `${theme.routes.cart_url}/shipping_rates.json`;

    // remove double `/` in case shop might have /en or language in URL
    sectionUrl = sectionUrl.replace('//', '/');

    fetch(sectionUrl, { ...fetchConfig('javascript'), body })
      .then((response) => response.json())
      .then((parsedState) => {
        if (parsedState.shipping_rates) {
          this.success.classList.remove('hidden');
          this.success.innerHTML = '';
          
          parsedState.shipping_rates.forEach((rate) => {
            const child = document.createElement('p');
            child.innerHTML = `${rate.name}: ${rate.price} ${Shopify.currency.active}`;
            this.success.appendChild(child);
          });
        }
        else {
          let errors = [];
          Object.entries(parsedState).forEach(([attribute, messages]) => {
            errors.push(`${attribute.charAt(0).toUpperCase() + attribute.slice(1)} ${messages[0]}`);
          });

          this.errors.classList.remove('hidden');
          this.errors.querySelector('.errors').innerHTML = errors.join('; ');
        }
      })
      .catch((e) => {
        console.error(e);
      })
      .finally(() => {
        this.button.classList.remove('loading');
        this.button.removeAttribute('disabled');
      });
  }
}

customElements.define('shipping-calculator', ShippingCalculator);
